import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@hortis/ui/button";
import {
  SheetBody,
  SheetDescription,
  SheetFooter,
  SheetForm,
  SheetHeader,
  SheetTitle,
} from "@hortis/ui/sheet";
import { toast } from "@hortis/ui/sonner";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { MissingValidationDialog } from "src/components/autocomplete/scientific-names/missing-validation-dialog";
import { match } from "ts-pattern";
import type { TaxonFieldsFragment } from "../../../../../../generated/graphql";
import { useUpdateTaxonMutation } from "../../../../../../generated/graphql";
import { type TaxonFields, taxonFieldsSchema } from "../../new-taxon/schema";
import { TaxonDataFields } from "../../new-taxon/taxon-data-fields";

export interface TaxonEditDrawerProps {
  taxon: TaxonFieldsFragment;
  open: boolean;
  requestClose: () => void;
  refetchTaxon: () => void;
}

export const EditTaxonSheetForm = ({
  taxon,
  requestClose,
  refetchTaxon,
  open,
}: TaxonEditDrawerProps) => {
  const [missingValidationDialogOpen, setMissingValidationDialogOpen] =
    useState(false);
  const formMethods = useForm<TaxonFields>({
    defaultValues: {
      ...taxon,
      tags:
        taxon.tagsConnection?.nodes.map(({ id, name }) => ({ id, name })) ?? [],
    },
    resolver: zodResolver(taxonFieldsSchema),
  });
  const sharedScientificName = formMethods.watch("sharedScientificName");

  useEffect(() => {
    formMethods.reset({
      ...taxon,
      tags:
        taxon.tagsConnection?.nodes.map(({ id, name }) => ({ id, name })) ?? [],
    });
  }, [open, formMethods, taxon]);

  const [, updateTaxon] = useUpdateTaxonMutation();

  const taxaInterpretationEnabled =
    taxon.organisation?.taxaInterpretationEnabled;

  const handleSubmit = useMemo(
    () =>
      formMethods.handleSubmit(async (_data) => {
        if (sharedScientificName == null && !missingValidationDialogOpen) {
          setMissingValidationDialogOpen(true);
          return;
        }
        try {
          const { sharedScientificName, ...data } = _data;
          const res = await updateTaxon({
            id: taxon.id,
            input: {
              ...data,
              sharedScientificNameId: sharedScientificName?.id ?? null,
              tags: data.tags.map(({ id }) => id),
            },
          });
          if (res.data?.updateTaxon.success === true) {
            toast.success("Successfully updated taxon");
            requestClose();
            refetchTaxon();
            return;
          }
          const errors = res.data?.updateTaxon.errors;
          if (errors != null && errors.length > 0) {
            errors.forEach((error) => {
              match(error.__typename)
                .with("DuplicateScientificNameError", () => {
                  formMethods.setError("scientificName", {
                    message: error.message,
                  });
                })
                .exhaustive();
            });
            return;
          }
          throw new Error("Failed to update taxon");
        } catch {
          toast.error("Failed to update taxon");
        }
      }),
    [
      formMethods,
      updateTaxon,
      taxon.id,
      requestClose,
      refetchTaxon,
      sharedScientificName,
      missingValidationDialogOpen,
    ],
  );

  return (
    <FormProvider {...formMethods}>
      <SheetForm onSubmit={handleSubmit}>
        <SheetHeader>
          <SheetTitle>Edit taxon</SheetTitle>
          <SheetDescription>Make changes to taxon data</SheetDescription>
        </SheetHeader>
        <SheetBody>
          <TaxonDataFields
            interpretationFieldsEnabled={taxaInterpretationEnabled}
          />
        </SheetBody>
        <SheetFooter>
          <Button
            testId="cancel-edit-button"
            onClick={() => {
              requestClose();
            }}
          >
            Cancel
          </Button>
          <MissingValidationDialog
            open={missingValidationDialogOpen}
            onOpenChange={setMissingValidationDialogOpen}
            onSubmit={handleSubmit}
            submitText="Save taxon"
          />
          <Button testId="save-taxon-button" type="submit" variant="primary">
            Save taxon
          </Button>
        </SheetFooter>
      </SheetForm>
    </FormProvider>
  );
};
